import React, { useEffect, useState, useCallback } from 'react';
import { buttonClass } from '../components/ui/Button';
import useScheduleStore, { defaultWindow } from '../stores/useScheduleStore';
import { useScheduleDerived } from './useScheduleDerived';
import ScheduleGrid from './ScheduleGrid';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';

/**
 * The read-only staff timeline, shown below a crew member's own shows on
 * /my-schedule (ff-feedback items 7 + 14).
 *
 * Her ask: "Pilots (and other crew) not being able to see their entire month
 * view. I get asked way to many questions" and "Crew-facing read only version of
 * the full staff schedule timeline."
 *
 * Same ScheduleGrid the producer uses, in readOnly mode, fed by
 * /schedule/crew-range whose payload is redacted server-side. The write paths are
 * off in three independent places — the endpoint cannot return producer data, the
 * grid substitutes no-op handlers, and each cell drops its own click/drag/drop.
 * Hiding buttons alone would not be a permission boundary.
 */
const CrewScheduleTimeline: React.FC = () => {
  const fetchCrewRange = useScheduleStore(s => s.fetchCrewRange);
  const loading = useScheduleStore(s => s.loading);
  const error = useScheduleStore(s => s.error);
  const staff = useScheduleStore(s => s.staff);

  const [collapsedEmpty, setCollapsedEmpty] = useState(true);
  const [jumpToken, setJumpToken] = useState(0);

  const {
    eventColorMap, eventLookup, eventsPerDay, oooByUserDay,
    effectiveDayBars, effectiveBlockBars, hasEmptyDays,
  } = useScheduleDerived();

  useEffect(() => {
    const { start, end } = defaultWindow();
    fetchCrewRange(start, end);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Required by the grid's signature but unreachable: readOnly drops the click
  // before it can fire.
  const noopCellClick = useCallback(() => {}, []);

  return (
    <div className="mt-10">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-on-surface day:text-gray-900">Everyone's schedule</h3>
          <p className="text-xs text-on-surface-variant day:text-gray-600 mt-0.5">
            Read only. Scroll sideways to move through the months.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setJumpToken(t => t + 1)}
            className={buttonClass('tonal', 'sm')}
          >
            Today
          </button>
          {hasEmptyDays && (
            <button
              onClick={() => setCollapsedEmpty(v => !v)}
              className={`px-2.5 py-1 text-xs rounded border transition-colors
                ${collapsedEmpty
                  ? 'border-primary/40 day:border-amber-500 text-primary-ink day:text-amber-700 bg-primary/5 day:bg-amber-50'
                  : 'border-gray-700 day:border-gray-300 text-on-surface-variant day:text-gray-700 hover:border-outline day:hover:border-gray-400'}`}
            >
              {collapsedEmpty ? 'Show all days' : 'Collapse empty'}
            </button>
          )}
        </div>
      </div>

      {loading && (
        <SkeletonGroup label="Loading everyone's schedule" className="space-y-2 py-4">
          {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-9 w-full" />)}
        </SkeletonGroup>
      )}

      {!loading && error && (
        <Alert className="my-4">{error}</Alert>
      )}

      {!loading && !error && !staff.length && (
        <EmptyState variant="area">No crew on the schedule yet.</EmptyState>
      )}

      {!loading && !error && !!staff.length && (
        <ScheduleGrid
          readOnly
          onCellClick={noopCellClick}
          collapsedEmpty={collapsedEmpty}
          eventColorMap={eventColorMap}
          eventLookup={eventLookup}
          eventsPerDay={eventsPerDay}
          oooByUserDay={oooByUserDay}
          effectiveDayBars={effectiveDayBars}
          /* Time off and scheduler blocks arrived merged and reason-free from
             /crew-range; they render through the block-bar path with empty notes,
             which is what makes them read as a plain "unavailable" stripe. */
          effectiveBlockBars={effectiveBlockBars}
          jumpToken={jumpToken}
        />
      )}
    </div>
  );
};

export default CrewScheduleTimeline;
